import type { PvpTeamEndedFrame, PvpTeamLobby } from '@kenocyber/shared';
import type { PvpClientFailure } from './connection';
import type { PvpAbandonPhase, PvpUiError } from './machine';
import type { PvpTeamClientState } from './team-connection';
import type { PvpTeamLetter } from './team-result-text';

/**
 * What the `team_3v3` interface is doing, as one value that cannot contradict
 * itself.
 *
 * ## Why a second machine rather than a flag on the duel's
 *
 * The two flows agree on their edges and disagree in the middle, and the middle
 * is where every rule lives. A duel waits on ONE code and a team lobby publishes
 * TWO; a duel seats two people the moment the second arrives and a team lobby
 * fills six chairs and then has to be FROZEN by its owner before any capability
 * can exist; a duel's result names a seat and this one names a team. Threading
 * that through `pvpReducer` would mean `if (team)` at six branches of a reducer
 * whose whole value is that every branch is exhaustive - and the first one
 * somebody forgot would be a screen a player sees rather than a test that fails.
 *
 * So this is its own union with its own events, and the duel's file is untouched.
 * What the two share is the vocabulary - {@link PvpUiError}, the transition into
 * the arena, the recovery of the hunt on the way out - because those are the same
 * decisions and two copies of them would drift.
 *
 * ## The lobby is the SERVER's answer, held whole
 *
 * `lobby` is a {@link PvpTeamLobby} exactly as the routes returned it: six
 * chairs, who is in them, whether it is full, whether it is frozen, the caller's
 * own chair, and - for the account that opened it and nobody else - the two
 * codes. Nothing is derived into a second field, so there is no place for a
 * client's opinion about who is on which team to live. Every read of "my side"
 * and "theirs" goes through `you.team`, which the server put there.
 *
 * ## Nothing here decides a result
 *
 * `completed` is only ever reached by an `ended` frame naming a `winnerTeam`. A
 * hull at zero health, a clock at zero, a closed socket and "the last team with
 * a ship on screen" are not results, and this machine has no transition that
 * could turn one into a winner.
 */

/* -------------------------------------------------------------------------- */
/*  What a chair is, once one exists                                          */
/* -------------------------------------------------------------------------- */

/**
 * The match this browser is in, and the chair the SERVER gave it.
 *
 * Every field is the authority's answer: the participant id comes from the
 * capability, the team and the slot come from the same row that minted it. There
 * is no constructor here that could produce one from a roster index, which is
 * the whole point - a reordered roster must never move anybody.
 */
export interface PvpTeamMatchIdentity {
  readonly matchId: string;
  readonly participantId: string;
  readonly team: PvpTeamLetter;
  /**
   * `1`..`3`, as the server assigned it, and typed as an integer rather than as
   * the union.
   *
   * The two authoritative sources disagree about the TYPE and not about the
   * value: the socket's `you.sl` is a union of three literals and the lobby's
   * `chairs[].slot` is a bounded integer. Narrowing here would mean a cast -
   * this client asserting a range it did not check - to gain nothing, because
   * the slot reaches exactly one decision on the whole path: the order two bars
   * are printed in.
   */
  readonly slot: number;
}

/* -------------------------------------------------------------------------- */
/*  The states                                                                */
/* -------------------------------------------------------------------------- */

export type PvpTeamUiState =
  /** The panel is not open. Nothing is held: no lobby, no socket, no lease. */
  | { readonly kind: 'closed' }
  /**
   * The lobby form, before a choice. The hunt is still running behind it.
   *
   * `lastError` is what a refused CODE leaves behind, and it lives here rather
   * than on an error screen for the same reason it does in the duel: a code the
   * server did not recognise is a typo, and a screen that replaces the form
   * costs the player the form.
   */
  | { readonly kind: 'choosing'; readonly lastError: PvpUiError | null }
  /** A lobby is being opened. */
  | { readonly kind: 'creating'; readonly operation: number }
  /** A code is being presented. */
  | { readonly kind: 'joining'; readonly operation: number }
  /**
   * A chair is held and the roster is filling. The hunt is STILL running.
   *
   * The one state with two codes in it, and only for the account that opened the
   * room - `lobby.codes` is `null` for everybody else, which is the server's
   * decision and not a rendering choice made here.
   *
   * `launching` is a request in flight, not a permission: whether this account
   * may freeze the roster is the SERVER's rule, and the refusal is its answer.
   */
  | {
      readonly kind: 'lobby';
      readonly lobby: PvpTeamLobby;
      readonly launching: boolean;
      readonly lastError: PvpUiError | null;
    }
  /** Frozen, capability minted, socket opening. Still no reason to disturb the hunt. */
  | {
      readonly kind: 'connecting';
      readonly match: PvpTeamMatchIdentity;
      readonly operation: number;
      /** Whether this socket is REJOINING a match that was already running. */
      readonly resumed: boolean;
    }
  /**
   * The room is complete and the dedicated transition is running.
   *
   * The one state where the hunt is being taken down and the arena is not yet
   * up. It carries how far the transition got, because a rollback has to undo
   * exactly what was done and nothing else.
   */
  | {
      readonly kind: 'entering_pvp';
      readonly match: PvpTeamMatchIdentity;
      readonly generation: number;
    }
  /** In the arena, and may declare itself ready. The room starts when all six have. */
  | {
      readonly kind: 'ready';
      readonly match: PvpTeamMatchIdentity;
      readonly generation: number;
      readonly connection: PvpTeamClientState;
      /** Whether a `ready` has already gone out for THIS generation. */
      readonly declared: boolean;
    }
  /** Fighting. The only state in which controls are enabled. */
  | {
      readonly kind: 'running';
      readonly match: PvpTeamMatchIdentity;
      readonly generation: number;
      readonly connection: PvpTeamClientState;
      /**
       * Whether this chair has asked to abandon, and how that is going (ADR 0043).
       *
       * A sub-state of `running` for the same reason it is one in the duel: the
       * match is still ticking and this ship is still being shot at while the
       * intent travels. What differs here is the CONSEQUENCE, not the mechanism -
       * one chair leaving ends the match in favour of the other team, because
       * there is no 2x3 contract to continue into.
       */
      readonly abandon: PvpAbandonPhase;
    }
  /** The socket dropped and the room is holding the chair. Controls are off. */
  | {
      readonly kind: 'reconnecting';
      readonly match: PvpTeamMatchIdentity;
      readonly generation: number;
      readonly connection: PvpTeamClientState;
    }
  /** The server named a winning team, or named none. The only way to hold a result. */
  | {
      readonly kind: 'completed';
      readonly match: PvpTeamMatchIdentity;
      readonly result: PvpTeamEndedFrame;
    }
  /** Tearing the match down. The hunt is still hidden and still paused. */
  | { readonly kind: 'leaving'; readonly match: PvpTeamMatchIdentity | null }
  /**
   * The match is gone and the hunt has not been re-authorised yet.
   *
   * Its own state rather than a flag on `leaving`: the hunt stays hidden and
   * paused throughout, and a failed bootstrap must be able to sit here offering
   * a retry instead of showing a hunt nobody has re-authorised.
   */
  | {
      readonly kind: 'recovering_hunt';
      readonly operation: number;
      readonly failure: PvpUiError | null;
    }
  | { readonly kind: 'error'; readonly error: PvpUiError };

export type PvpTeamUiStateKind = PvpTeamUiState['kind'];

/* -------------------------------------------------------------------------- */
/*  The events                                                                */
/* -------------------------------------------------------------------------- */

export type PvpTeamUiEvent =
  | { readonly type: 'OPEN' }
  | { readonly type: 'CREATE_REQUESTED'; readonly operation: number }
  | {
      readonly type: 'CREATE_SUCCEEDED';
      readonly operation: number;
      readonly lobby: PvpTeamLobby;
    }
  | { readonly type: 'CREATE_FAILED'; readonly operation: number; readonly error: PvpUiError }
  | { readonly type: 'JOIN_REQUESTED'; readonly operation: number }
  | { readonly type: 'JOIN_SUCCEEDED'; readonly operation: number; readonly lobby: PvpTeamLobby }
  | { readonly type: 'JOIN_FAILED'; readonly operation: number; readonly error: PvpUiError }
  /**
   * A fresh read of the roster, from `GET /api/pvp/team/lobby`.
   *
   * The lobby has no socket of its own - it is six database rows - so the only
   * honest way to see somebody arrive is to ask again. The controller polls
   * while this state holds and stops the moment it does not.
   */
  | { readonly type: 'LOBBY_UPDATED'; readonly lobby: PvpTeamLobby }
  /**
   * The lobby is gone: cancelled by its owner, expired, or this account left it
   * in another tab. Not an error the player did anything about.
   */
  | { readonly type: 'LOBBY_LOST' }
  | { readonly type: 'LAUNCH_REQUESTED' }
  | { readonly type: 'LAUNCH_FAILED'; readonly error: PvpUiError }
  /**
   * The server says this account is already in a team lobby, and described it.
   *
   * Sent after a reload, from an authoritative read rather than from anything
   * the tab remembered. A frozen lobby goes on to connect; one that is still
   * filling goes back to showing the roster.
   */
  | { readonly type: 'RESUME_LOBBY'; readonly operation: number; readonly lobby: PvpTeamLobby }
  | {
      readonly type: 'CONNECT_STARTED';
      readonly operation: number;
      readonly match: PvpTeamMatchIdentity;
      readonly resumed: boolean;
    }
  | {
      readonly type: 'AUTHORITATIVE_STATE';
      readonly generation: number;
      readonly state: PvpTeamClientState;
    }
  | {
      readonly type: 'ENTER_STARTED';
      readonly generation: number;
      readonly match: PvpTeamMatchIdentity;
    }
  | {
      readonly type: 'ENTERED';
      readonly generation: number;
      readonly state: PvpTeamClientState;
    }
  | { readonly type: 'ENTER_FAILED'; readonly generation: number; readonly error: PvpUiError }
  | { readonly type: 'READY_REQUESTED'; readonly generation: number }
  | { readonly type: 'ENDED'; readonly generation: number; readonly result: PvpTeamEndedFrame }
  | {
      readonly type: 'CONNECTION_FAILED';
      readonly generation: number;
      readonly failure: PvpClientFailure;
      readonly detail: string | null;
    }
  /** The chair pressed ABANDONAR PARTIDA and the intent is going out (ADR 0043). */
  | { readonly type: 'ABANDON_REQUESTED'; readonly generation: number }
  /** The intent could not be sent. The chair is still in the match. */
  | { readonly type: 'ABANDON_FAILED'; readonly generation: number }
  | { readonly type: 'LEAVE_REQUESTED' }
  | { readonly type: 'HUNT_RECOVERY_STARTED'; readonly operation: number }
  | { readonly type: 'HUNT_RECOVERED'; readonly operation: number }
  | {
      readonly type: 'HUNT_RECOVERY_FAILED';
      readonly operation: number;
      readonly error: PvpUiError;
    }
  | { readonly type: 'RESET_ERROR' }
  /** The player started typing a different code, so the last refusal stops applying. */
  | { readonly type: 'CODE_EDITED' }
  | { readonly type: 'CLOSED' };

/* -------------------------------------------------------------------------- */
/*  Guards                                                                    */
/* -------------------------------------------------------------------------- */

/** Whether the match currently holds the hunt paused and hidden. */
export function teamHoldsHunt(state: PvpTeamUiState): boolean {
  switch (state.kind) {
    case 'entering_pvp':
    case 'ready':
    case 'running':
    case 'reconnecting':
    case 'completed':
    case 'leaving':
    case 'recovering_hunt':
      return true;
    default:
      /*
       * The form, a lobby filling up and a socket that has not reached a
       * complete room all leave the hunt alone. A player waiting on five other
       * people has not left the game.
       */
      return false;
  }
}

/** Whether the arena is open, so a close must be published on the way out. */
export function teamArenaIsOpen(state: PvpTeamUiState): boolean {
  switch (state.kind) {
    case 'ready':
    case 'running':
    case 'reconnecting':
    case 'completed':
      return true;
    default:
      // `entering_pvp` deliberately answers false: the transition publishes
      // `pvp:open` at its last step, and a rollback before that must not close
      // a scene that was never opened.
      return false;
  }
}

/** Whether a player may drive their ship. Only ever while the room is running. */
export function teamControlsEnabled(state: PvpTeamUiState): boolean {
  return state.kind === 'running';
}

/** The chair a state is about, if it is about one. */
export function teamMatchOf(state: PvpTeamUiState): PvpTeamMatchIdentity | null {
  switch (state.kind) {
    case 'connecting':
    case 'entering_pvp':
    case 'ready':
    case 'running':
    case 'reconnecting':
    case 'completed':
    case 'leaving':
      return state.match;
    default:
      return null;
  }
}

export function teamGenerationOf(state: PvpTeamUiState): number | null {
  switch (state.kind) {
    case 'entering_pvp':
    case 'ready':
    case 'running':
    case 'reconnecting':
      return state.generation;
    default:
      return null;
  }
}

/**
 * Whether this account may freeze the roster, as the SERVER decides it.
 *
 * `POST /api/pvp/team/launch` refuses anybody who is not `a1` with
 * `not_the_lobby_owner`, and this is that same sentence - read from the chair the
 * server put in `you`, never from who happened to click first or from which
 * browser opened the panel. It is presentation only: the button it hides is a
 * button the API would refuse anyway.
 */
export function teamOwnsLobby(lobby: PvpTeamLobby): boolean {
  return lobby.you.team === 'a' && lobby.you.slot === 1;
}

/**
 * Whether the launch may be attempted right now.
 *
 * Owner, six chairs taken, and not already frozen. `full` is the server's own
 * derived field rather than a count made here, because the roster is what
 * decides and a client that counted occupied chairs would be a second opinion
 * about the same list.
 */
export function teamCanLaunch(lobby: PvpTeamLobby): boolean {
  return teamOwnsLobby(lobby) && lobby.full && !lobby.frozen;
}

/** Whether an answer from an asynchronous operation is still wanted. */
function operationMatches(state: PvpTeamUiState, operation: number): boolean {
  switch (state.kind) {
    case 'creating':
    case 'joining':
    case 'connecting':
    case 'recovering_hunt':
      return state.operation === operation;
    default:
      return false;
  }
}

/** Whether a socket-borne event belongs to the connection the machine holds. */
function generationMatches(state: PvpTeamUiState, generation: number): boolean {
  const current = teamGenerationOf(state);
  return current !== null && current === generation;
}

/* -------------------------------------------------------------------------- */
/*  The reducer                                                               */
/* -------------------------------------------------------------------------- */

export const PVP_TEAM_INITIAL_STATE: PvpTeamUiState = { kind: 'closed' };

/**
 * The whole machine.
 *
 * Every branch that returns `state` unchanged is a transition somebody could
 * have written and should not have: a roster poll answering after the player
 * left, a welcome from a socket that has been replaced, an ending for a match
 * that is not the one on screen. Dropping them here - once - is what keeps the
 * controller from having to remember to check.
 */
export function pvpTeamReducer(state: PvpTeamUiState, event: PvpTeamUiEvent): PvpTeamUiState {
  switch (event.type) {
    case 'OPEN':
      // Only from closed. Opening a panel that is already open must not throw
      // away a lobby in progress.
      return state.kind === 'closed' ? { kind: 'choosing', lastError: null } : state;

    case 'CREATE_REQUESTED':
      return state.kind === 'choosing' ? { kind: 'creating', operation: event.operation } : state;

    case 'CREATE_SUCCEEDED':
      if (!operationMatches(state, event.operation) || state.kind !== 'creating') {
        return state;
      }
      return { kind: 'lobby', lobby: event.lobby, launching: false, lastError: null };

    case 'CREATE_FAILED':
      if (!operationMatches(state, event.operation) || state.kind !== 'creating') {
        return state;
      }
      /*
       * Back to the FORM, carrying the refusal.
       *
       * Nothing was taken by a create that failed - no chair, no lease, no
       * socket - so there is nothing to unwind and nowhere to go except the
       * screen the player was already looking at. `already_in_match` in
       * particular is a thing they can act on.
       */
      return { kind: 'choosing', lastError: event.error };

    case 'JOIN_REQUESTED':
      return state.kind === 'choosing' ? { kind: 'joining', operation: event.operation } : state;

    case 'JOIN_SUCCEEDED':
      if (!operationMatches(state, event.operation) || state.kind !== 'joining') {
        return state;
      }
      return { kind: 'lobby', lobby: event.lobby, launching: false, lastError: null };

    case 'JOIN_FAILED':
      if (!operationMatches(state, event.operation) || state.kind !== 'joining') {
        // An answer to an operation nobody is waiting for any more.
        return state;
      }
      return { kind: 'choosing', lastError: event.error };

    case 'RESUME_LOBBY':
      /*
       * Only from `choosing`, and the operation is carried INTO the new state
       * rather than checked against the old one. A player who reached for
       * something else while the answer was in flight has already told us what
       * they want.
       */
      if (state.kind !== 'choosing') {
        return state;
      }
      return { kind: 'lobby', lobby: event.lobby, launching: false, lastError: null };

    case 'LOBBY_UPDATED':
      if (state.kind !== 'lobby') {
        return state;
      }
      /*
       * A fresh roster replaces the old one WHOLE, and clears nothing else.
       *
       * `launching` survives: a poll that lands while the freeze is in flight
       * must not re-enable a button whose request is still out. `lastError`
       * survives for the same reason - it is about something the player did, and
       * a background read is not an answer to it.
       */
      return { ...state, lobby: event.lobby };

    case 'LOBBY_LOST':
      if (state.kind !== 'lobby') {
        return state;
      }
      // Back to the form with a message, never to an error screen: opening
      // another room is one click away and nothing was held.
      return {
        kind: 'choosing',
        lastError: { reason: 'team_lobby_gone', recoverable: true },
      };

    case 'LAUNCH_REQUESTED':
      if (state.kind !== 'lobby' || state.launching) {
        // One launch at a time. A second click is not a second room.
        return state;
      }
      return { ...state, launching: true, lastError: null };

    case 'LAUNCH_FAILED':
      if (state.kind !== 'lobby') {
        return state;
      }
      // Stays in the lobby, which is where the player still is: a refused
      // freeze took nothing and the roster is still on screen.
      return { ...state, launching: false, lastError: event.error };

    case 'CONNECT_STARTED':
      if (state.kind !== 'lobby' && state.kind !== 'choosing') {
        return state;
      }
      return {
        kind: 'connecting',
        match: event.match,
        operation: event.operation,
        resumed: event.resumed,
      };

    case 'AUTHORITATIVE_STATE':
      return applyAuthoritative(state, event.generation, event.state);

    case 'ENTER_STARTED':
      if (state.kind !== 'connecting') {
        // Only from a chair whose socket is up. Anything else is a duplicate, or
        // a room that is already drawn.
        return state;
      }
      return { kind: 'entering_pvp', match: event.match, generation: event.generation };

    case 'ENTERED':
      if (state.kind !== 'entering_pvp' || state.generation !== event.generation) {
        return state;
      }
      return {
        kind: 'ready',
        match: state.match,
        generation: state.generation,
        connection: event.state,
        declared: false,
      };

    case 'ENTER_FAILED':
      if (state.kind !== 'entering_pvp' || state.generation !== event.generation) {
        return state;
      }
      return { kind: 'error', error: event.error };

    case 'READY_REQUESTED':
      if (state.kind !== 'ready' || state.generation !== event.generation || state.declared) {
        // One `ready` per generation. A second click is not a second player.
        return state;
      }
      return { ...state, declared: true };

    case 'ENDED': {
      if (!generationMatches(state, event.generation)) {
        return state;
      }
      const match = teamMatchOf(state);
      if (!match) {
        return state;
      }
      // The ONLY road to a result, and it comes from the server naming one.
      return { kind: 'completed', match, result: event.result };
    }

    case 'CONNECTION_FAILED':
      if (!generationMatches(state, event.generation) && state.kind !== 'connecting') {
        return state;
      }
      return {
        kind: 'error',
        error: { reason: event.detail ?? event.failure, recoverable: isRecoverable(event.failure) },
      };

    case 'ABANDON_REQUESTED':
      /*
       * Only from a fight, only for the connection this machine holds, and only
       * once at a time. Six chairs pressing at once is six intents from six
       * clients; ONE chair pressing six times is one intent.
       */
      if (
        state.kind !== 'running' ||
        state.generation !== event.generation ||
        state.abandon === 'sending'
      ) {
        return state;
      }
      return { ...state, abandon: 'sending' };

    case 'ABANDON_FAILED':
      if (
        state.kind !== 'running' ||
        state.generation !== event.generation ||
        state.abandon !== 'sending'
      ) {
        return state;
      }
      return { ...state, abandon: 'failed' };

    case 'LEAVE_REQUESTED':
      if (state.kind === 'closed' || state.kind === 'leaving') {
        return state;
      }
      if (!teamHoldsHunt(state)) {
        /*
         * Nothing was taken from the hunt, so nothing has to be given back:
         * no lease, no visibility message and no bootstrap. Leaving a lobby is
         * closing a panel - the CHAIR is given up by the controller, through
         * the official route.
         */
        return { kind: 'closed' };
      }
      return { kind: 'leaving', match: teamMatchOf(state) };

    case 'HUNT_RECOVERY_STARTED':
      if (state.kind !== 'leaving' && state.kind !== 'recovering_hunt') {
        return state;
      }
      return { kind: 'recovering_hunt', operation: event.operation, failure: null };

    case 'HUNT_RECOVERED':
      if (!operationMatches(state, event.operation) || state.kind !== 'recovering_hunt') {
        return state;
      }
      return { kind: 'closed' };

    case 'HUNT_RECOVERY_FAILED':
      if (!operationMatches(state, event.operation) || state.kind !== 'recovering_hunt') {
        return state;
      }
      // Stays here, holding the hunt hidden and paused, offering a retry.
      return { ...state, failure: event.error };

    case 'CODE_EDITED':
      return state.kind === 'choosing' && state.lastError !== null
        ? { kind: 'choosing', lastError: null }
        : state;

    case 'RESET_ERROR':
      return state.kind === 'error' ? { kind: 'choosing', lastError: null } : state;

    case 'CLOSED':
      return { kind: 'closed' };
  }
}

/**
 * Folds an authoritative client state into the interface.
 *
 * The match's own lifecycle - `ready`, `running` - is the server's and arrives
 * here; what this decides is only which SCREEN corresponds to it, and it refuses
 * anything from a generation it is not holding.
 */
function applyAuthoritative(
  state: PvpTeamUiState,
  generation: number,
  next: PvpTeamClientState,
): PvpTeamUiState {
  if (state.kind === 'connecting') {
    // The controller starts the dedicated transition when the room is complete.
    return state;
  }
  if (!generationMatches(state, generation)) {
    /*
     * A socket that has been replaced, still talking. The room applies the same
     * rule to a superseded chair.
     */
    return state;
  }
  switch (state.kind) {
    case 'ready':
    case 'running':
    case 'reconnecting': {
      const base = { match: state.match, generation: state.generation, connection: next };
      if (next.world.phase === 'connecting' || next.world.phase === 'authorising') {
        return { kind: 'reconnecting', ...base };
      }
      if (next.world.matchState === 'running') {
        /*
         * An abandonment in flight SURVIVES an authoritative frame, and does NOT
         * survive a reconnection - exactly as in the duel, and for the same
         * reason: rebuilding `running` from the frame alone would clear the busy
         * flag within 50 ms and the next click would send a second intent.
         */
        return {
          kind: 'running',
          ...base,
          abandon: state.kind === 'running' ? state.abandon : 'idle',
        };
      }
      if (state.kind === 'ready') {
        return { ...state, connection: next };
      }
      // Back to ready from a reconnection: the room has not started ticking.
      return { kind: 'ready', ...base, declared: false };
    }
    default:
      return state;
  }
}

/**
 * Whether a connection failure is worth offering to retry.
 *
 * The same list as the duel's, and for the same reasons - the definitive ones
 * are the server saying no on purpose.
 */
function isRecoverable(failure: PvpClientFailure): boolean {
  switch (failure) {
    case 'pvp_disabled':
    case 'unauthorised':
    case 'rate_limited':
    case 'malformed':
    case 'superseded':
    case 'rules_version_mismatch':
      return false;
    default:
      return true;
  }
}
